import { useEffect, useRef, useState } from 'react';
import { money } from '../api.js';
import { SECTIONS, canView } from '../lib/views.js';
import { ViewLink } from './ViewLink.jsx';
import { menuColumns, nextIndex, isDismiss, isActivate } from '../lib/menu.js';

/**
 * The persistent frame: client picker, navigation, sign out.
 *
 * VIEW_CAPABILITY, SECTIONS and canView live in ../lib/views.js and are
 * re-exported here so every existing import still resolves. They moved for one
 * reason: `npm test -w web` is node's own runner over `src/lib/*.test.js` and
 * node cannot parse JSX, so while that table sat in this file the rule that an
 * unknown view is shown to nobody — and the client_user blind-sheet case the
 * stock module depends on — could only be checked by eye.
 */
export { VIEW_CAPABILITY, SECTIONS, canView } from '../lib/views.js';

export function Shell({
  user, tenant, entities, entityId, onEntityChange,
  view, onView, onSignOut, isPracticeUser, canUseCode = false, children,
}) {
  const entity = entities.find((e) => e.id === entityId);
  const caps = entity?.capabilities ?? [];

  const canSee = (v) => canView(v, caps);
  const canManagePeople = caps.includes('people.practice') || caps.includes('people.client');
  // A key spans clients, which is what makes minting one privileged — the
  // server checks keys.manage on ANY active entity in the tenant, not the
  // one currently selected (see capabilities.js / apiKeyAdmin.js). Gating
  // this link on the selected entity's capabilities would make the UI
  // disagree with the API the moment an admin switched to a company where
  // their own role happens to be lower — so this checks across ALL entities,
  // the same shape canAddClient already uses for entity.create below.
  const canManageKeys = entities.some((e) => e.capabilities?.includes('keys.manage'));
  const canAddClient = entities.some((e) => e.capabilities?.includes('entity.create'));

  // A client has one company; a switcher of one is just a label with extra
  // clicks. The practice keeps its switcher because moving between clients is
  // the single most-used control they have.
  const oneCompany = entities.length === 1;

  // The one piece of state a disclosure bar needs: which panel is open, keyed
  // by its SECTIONS heading, or 'account', or null for none. All the DECISIONS
  // about what that key means for layout and keyboard movement live in
  // lib/menu.js (menuColumns, nextIndex, isDismiss, isActivate) and are only
  // ever called here, never reimplemented.
  const [open, setOpen] = useState(null);
  const headerRef = useRef(null);
  const triggerRefs = useRef({});
  // The flat, in-reading-order list of the currently open panel's item
  // controls. Rebuilt every time a panel renders open (see the reset just
  // before each panel's items below) — only one panel is ever open, so one
  // array is enough.
  const itemRefsRef = useRef([]);
  const pendingFocusFirstRef = useRef(false);

  // A mousedown anywhere outside the header closes whatever is open. Attached
  // only while something is open, and removed on close and on unmount.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // A trigger opened by keyboard (Enter/Space, flagged by isActivate in
  // onTriggerKeyDown below) moves focus into its first item once the panel
  // has rendered; one opened by a click or by hover-switching from another
  // open trigger leaves focus where it is.
  useEffect(() => {
    if (open && pendingFocusFirstRef.current) itemRefsRef.current[0]?.focus();
    pendingFocusFirstRef.current = false;
  }, [open]);

  const toggle = (key) => setOpen((cur) => (cur === key ? null : key));

  const closeAndReturnFocus = (key) => {
    setOpen(null);
    triggerRefs.current[key]?.focus();
  };

  // "While a panel is open, moving the pointer onto a different trigger
  // switches to it." Deliberately does nothing when nothing is open — hover
  // alone must not open a panel, since that fires on a pointer merely
  // crossing the bar and has no meaning on a touch screen.
  const onTriggerEnter = (key) => {
    if (open && open !== key) setOpen(key);
  };

  // Native <button>s already turn Enter and Space into a click on their own,
  // so isActivate is not needed to activate an already-open panel's items —
  // the browser does that. It IS needed here, on the trigger: opening the
  // panel and moving focus to its first item is not automatic, so this flags
  // the keys about to produce that click as the ones that should also move
  // focus once the panel exists.
  const onTriggerKeyDown = (key, e) => {
    if (isActivate(e.key)) pendingFocusFirstRef.current = true;
  };

  // Escape, and arrow-key movement between an open panel's items. Shared by
  // the trigger — where focus starts, at nextIndex's "-1, nothing focused
  // yet" position — and every item button in its panel, since both can be
  // the keydown's target.
  const onPanelKeyDown = (key, e) => {
    if (open !== key) return;
    if (isDismiss(e.key)) {
      closeAndReturnFocus(key);
      return;
    }
    const items = itemRefsRef.current;
    const current = items.indexOf(document.activeElement);
    const next = nextIndex(e.key, current, items.length);
    // Only a NUMBER back from nextIndex gates preventDefault. It returns null
    // for any key it does not handle, Tab included, and that is what keeps
    // Tab moving focus on to the next control instead of the panel trapping
    // it — a key nextIndex ignores must reach the browser's own handling.
    if (next !== null) {
      e.preventDefault();
      items[next]?.focus();
    }
  };

  // Choosing any item calls its action and then closes the panel.
  const choose = (action) => { action(); setOpen(null); };

  // A native <button> turns Space into a click; an <a> only does it for Enter.
  // The panel items used to be buttons, so keep Space working on the links.
  const spaceClicks = (e) => {
    if (e.key === ' ' && e.currentTarget.tagName === 'A') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const accountHeading = isPracticeUser ? (tenant?.name ?? 'Practice') : 'Your account';
  // Every item but Sign out is a view, and so a real link; Sign out is an
  // action and stays a button (its id is not a view, hence `isView`).
  const accountItems = [
    canAddClient && ['new-entity', 'Add a client/company'],
    canManagePeople && ['people', 'People'],
    canManageKeys && ['keys', 'API keys'],
    // Drawn only when the server said this person is on the Code allowlist.
    canUseCode && ['code', 'Code'],
    ['account', 'Your password'],
    ['sign-out', 'Sign out', user?.fullName],
  ].filter(Boolean);
  const accountOpen = open === 'account';
  if (accountOpen) itemRefsRef.current = [];

  return (
    <div className="app">
      <header className="topbar" ref={headerRef}>
        <div className="topbar__brand">Ledger</div>

        {/*
          A disclosure pattern (button + aria-expanded/haspopup/controls,
          panel of ordinary buttons) and NOT role="menu" / role="menuitem".
          These are navigation controls, not an application menu, and
          role="menu" changes both what a screen-reader user is told AND what
          keyboard behaviour they are entitled to expect — a real menu does
          not let Tab move between items, and this one does (see
          onPanelKeyDown above). Claiming the role without implementing its
          full key contract would be worse than not claiming it.
        */}
        <nav className="menubar" aria-label="Main">
          <ViewLink className="menubar__trigger" view="dashboard" entityId={entityId}
                    aria-current={view === 'dashboard' ? 'page' : undefined}
                    onNavigate={() => { setOpen(null); onView('dashboard'); }}>
            Home
          </ViewLink>

          {SECTIONS.map(([heading, items]) => {
            const visible = items.filter(([id]) => canSee(id));
            // A trigger opening an empty panel is worse than no trigger.
            if (visible.length === 0) return null;

            const columns = menuColumns(heading, visible);
            const isCurrentSection = visible.some(([id]) => id === view);
            const isOpen = open === heading;
            const panelId = `menu-panel-${heading}`;
            if (isOpen) itemRefsRef.current = [];
            let idx = -1;

            return (
              <div className="menubar__group" key={heading}>
                <button
                  ref={(el) => { triggerRefs.current[heading] = el; }}
                  className={`menubar__trigger${isCurrentSection ? ' menubar__trigger--current' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  // Only while the panel is in the DOM. aria-controls must
                  // reference an element that exists; these panels render
                  // only when open, so pointing at a missing id the rest of
                  // the time is a dangling reference. aria-expanded alone is
                  // a complete disclosure contract without it.
                  aria-controls={isOpen ? panelId : undefined}
                  onClick={() => toggle(heading)}
                  onMouseEnter={() => onTriggerEnter(heading)}
                  onKeyDown={(e) => { onTriggerKeyDown(heading, e); onPanelKeyDown(heading, e); }}
                >
                  {heading}
                </button>
                {isOpen && (
                  <div className="menubar__panel" id={panelId}>
                    {columns.map(([label, colItems], ci) => (
                      <div className="menubar__column" key={label ?? `col-${ci}`}>
                        {label && <div className="menubar__heading">{label}</div>}
                        {colItems.map(([id, itemLabel]) => {
                          idx += 1;
                          const itemIdx = idx;
                          return (
                            <ViewLink
                              key={id}
                              ref={(el) => { itemRefsRef.current[itemIdx] = el; }}
                              className="menubar__item"
                              view={id} entityId={entityId}
                              aria-current={view === id ? 'page' : undefined}
                              onNavigate={() => choose(() => onView(id))}
                              onKeyDown={(e) => { spaceClicks(e); onPanelKeyDown(heading, e); }}
                            >
                              {itemLabel}
                            </ViewLink>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="topbar__right">
          <div className="picker">
            <span className="picker__label visually-hidden">
              {isPracticeUser ? 'Client company' : 'Company'}
            </span>
            {oneCompany ? (
              <div style={{
                padding: '9px 10px', background: '#11161C', border: '1px solid #2C353E',
                color: '#fff', fontSize: 13,
              }}>
                {entities[0]?.name ?? 'No company'}
              </div>
            ) : (
              <select id="client" aria-label="Choose a company"
                      value={entityId ?? ''} onChange={(e) => onEntityChange(e.target.value)}>
                {entities.length === 0 && <option value="">No companies yet</option>}
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
          </div>

          <div className="menubar__group">
            <button
              ref={(el) => { triggerRefs.current.account = el; }}
              className="menubar__trigger"
              aria-haspopup="true"
              aria-expanded={accountOpen}
              aria-controls={accountOpen ? 'menu-panel-account' : undefined}
              onClick={() => toggle('account')}
              onMouseEnter={() => onTriggerEnter('account')}
              onKeyDown={(e) => { onTriggerKeyDown('account', e); onPanelKeyDown('account', e); }}
            >
              {/* A visible word beside the glyph, and NO aria-label. A bare
                  gear is a guess for someone hunting "Sign out", and an
                  aria-label over visible text replaces the accessible name
                  with something the label does not say — so anyone driving
                  this by voice would ask for what they can read and miss. */}
              <span aria-hidden="true">⚙</span>
              Account
            </button>
            {accountOpen && (
              <div className="menubar__panel menubar__panel--right" id="menu-panel-account">
                <div className="menubar__column">
                  <div className="menubar__heading">{accountHeading}</div>
                  {accountItems.map(([id, label, sub], i) => {
                    const content = (
                      <>
                        {label}
                        {sub && <span style={{ opacity: 0.6, fontSize: 11 }}>{sub}</span>}
                      </>
                    );
                    return id === 'sign-out' ? (
                      <button
                        key={id}
                        ref={(el) => { itemRefsRef.current[i] = el; }}
                        className="menubar__item"
                        onClick={() => choose(onSignOut)}
                        onKeyDown={(e) => onPanelKeyDown('account', e)}
                      >
                        {content}
                      </button>
                    ) : (
                      <ViewLink
                        key={id}
                        ref={(el) => { itemRefsRef.current[i] = el; }}
                        className="menubar__item"
                        view={id} entityId={entityId}
                        aria-current={view === id ? 'page' : undefined}
                        onNavigate={() => choose(() => onView(id))}
                        onKeyDown={(e) => { spaceClicks(e); onPanelKeyDown('account', e); }}
                      >
                        {content}
                      </ViewLink>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main">{children}</main>
    </div>
  );
}

/** Report header: whose books, which statement, over what period. */
export function ReportHead({ entity, title, sub }) {
  return (
    <header className="rhead">
      <div className="rhead__entity">
        {entity?.name}
        {entity?.is_vat_registered && entity?.vat_number
          ? <> · VAT {entity.vat_number}</> : null}
      </div>
      <h1 className="rhead__title display">{title}</h1>
      {sub && <div className="rhead__sub">{sub}</div>}
    </header>
  );
}

/**
 * The balance rule — the signature element.
 *
 * States plainly whether the statement balances and by how much. It is always
 * present, not only when something is wrong, because "did it balance?" is the
 * first question asked of every statement and an absent answer is not an
 * answer.
 */
export function BalanceRule({ difference, balancedLabel = 'Balanced', context }) {
  const off = !/^-?0\.00$/.test(String(difference ?? '0.00'));
  return (
    <div className={`balance${off ? ' balance--off' : ''}`}>
      <span className="balance__state">{off ? 'Out of balance' : balancedLabel}</span>
      <span className="balance__detail">
        {off ? (
          <>Difference <strong>{money(difference)}</strong> — the ledger should never produce this. Check for a partly captured entry before relying on this statement.</>
        ) : (
          context ?? <>Debits equal credits.</>
        )}
      </span>
    </div>
  );
}

export function Money({ value, className = '' }) {
  const neg = String(value ?? '').startsWith('-');
  return <span className={`figure ${neg ? 'neg' : ''} ${className}`}>{money(value)}</span>;
}

export function Empty({ children }) {
  return <div className="empty">{children}</div>;
}
